import { useState } from 'react';
import { apiWrite } from '../../lib/api-client.js';
import type { Permission } from './use-permissions-read-model.js';

const AUTH_CODE = '1337';

interface PermWriteResponse {
  active?: Permission[];
  revoked?: Permission[];
  error?: string;
}

export function usePermissionsWrite(
  onResult: (data: { active?: Permission[]; revoked?: Permission[] }) => void,
) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function grantPermission(
    targetEmail: string,
    module: string,
    canGrant: boolean,
    notes?: string,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<PermWriteResponse>('/api/permissions/grant', 'POST', {
        target_email: targetEmail,
        module,
        can_grant: canGrant,
        authorization_code: AUTH_CODE,
        notes: notes || null,
      });
      if (!ok || data.error) {
        setError(data.error || 'Grant failed');
        return false;
      }
      onResult(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function revokePermission(targetEmail: string, module: string): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<PermWriteResponse>('/api/permissions/revoke', 'POST', {
        target_email: targetEmail,
        module,
        authorization_code: AUTH_CODE,
      });
      if (!ok || data.error) {
        setError(data.error || 'Revoke failed');
        return false;
      }
      onResult(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function revokeAll(targetEmail: string): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<PermWriteResponse>('/api/permissions/revoke-all', 'POST', {
        target_email: targetEmail,
        authorization_code: AUTH_CODE,
      });
      if (!ok || data.error) {
        setError(data.error || 'Revoke-all failed');
        return false;
      }
      onResult(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    grantPermission,
    revokePermission,
    revokeAll,
    submitting,
    error,
    clearError: () => setError(null),
  };
}
